import type { ZoneFeatures } from 'src/config/types';
import { isATargetDevice } from 'src/modules/app-install-banner';
import type { CookieType } from 'src/types/cookie';

import { toggleExperiment } from '../experiments/experimentHelpers';
import type { TSeekExperiments } from '../experiments/types';
import type { TypedAction } from '../types';

import {
  OVERRIDE_FEATURE_FLAG,
  UPDATE_FEATURE_FLAGS,
  type FeatureFlagsState,
  type OverrideFeatureFlagAction,
  type UpdateFeatureFlagsAction,
} from './types';

export const initialState: FeatureFlagsState = {
  branchBannerPreview: false,
  isBranchEnabledFlag: false,
  showHomepageBanner: false,
  showFloatingSaveSearch: true,
  autoSelectSplitViewFirstJob: true,
  showMarketingAdSplitView: false,
  loggedOutRecs: false,
  remoteSearchFilter: false,
  remoteSearchFilterNewLabel: false,
  keywordAutosuggestV2: false,
  serpJobCardInfoDensity1: false,
  hideCompanyLogo: false,
  entryLevelBadge: false,
  homepageRecsBadgingStrongApplicant: false,
  homepageJobCardDensity: false,
  homepageGenAIChat: false,
  homepageGenAIChatAltModel: false,
  homepageGenAIChatAltPosition: false,
  homepageGenAIChatMobileDrawer: false,
  homepageGenAIChatDesktopDrawer: false,
  competitivePlacementEmbeddedJdv: false,
  competitivePlacement: false,
  homeFeedAPI: false,
};

export const initialiseFeatureFlags = ({
  cookies = {},
  experiments,
  zoneFeatures: {
    SHOW_FLOATING_SAVE_SEARCH,
    AUTO_SELECT_SPLIT_VIEW_FIRST_JOB,
    SHOW_MARKETING_AD_SPLIT_VIEW,
    LOGGED_OUT_RECS,
    REMOTE_SEARCH_FILTER,
    REMOTE_SEARCH_FILTER_NEW_LABEL,
    KEYWORD_AUTOSUGGEST_V2,
    SERP_JOBCARD_INFO_DENSITY_1,
    ENTRY_LEVEL_BADGE,
  },
  userAgent,
}: {
  cookies: CookieType;
  experiments: TSeekExperiments;
  zoneFeatures: ZoneFeatures;
  userAgent: string;
}): FeatureFlagsState => ({
  ...initialState,
  branchBannerPreview: Boolean(cookies.BRANCH_BANNER_PREVIEW),
  isBranchEnabledFlag:
    Boolean(cookies.ENABLE_BRANCH_BANNER) || isATargetDevice(userAgent),
  showFloatingSaveSearch: Boolean(
    cookies.SHOW_FLOATING_SAVE_SEARCH || SHOW_FLOATING_SAVE_SEARCH,
  ),
  autoSelectSplitViewFirstJob:
    Boolean(cookies.AUTO_SELECT_SPLIT_VIEW_FIRST_JOB) ||
    Boolean(AUTO_SELECT_SPLIT_VIEW_FIRST_JOB),
  showMarketingAdSplitView: Boolean(
    cookies.SHOW_MARKETING_AD_SPLIT_VIEW || SHOW_MARKETING_AD_SPLIT_VIEW,
  ),
  loggedOutRecs: Boolean(cookies.LOGGED_OUT_RECS || LOGGED_OUT_RECS),
  remoteSearchFilter:
    Boolean(cookies.REMOTE_SEARCH_FILTER || REMOTE_SEARCH_FILTER) ||
    toggleExperiment({
      experimentId: 'remoteSearchFilter',
      experiments,
      cookieValue: cookies.REMOTE_SEARCH_FILTER,
    }),
  remoteSearchFilterNewLabel: Boolean(REMOTE_SEARCH_FILTER_NEW_LABEL),
  keywordAutosuggestV2: Boolean(
    cookies.KEYWORD_AUTOSUGGEST_V2 || KEYWORD_AUTOSUGGEST_V2,
  ),
  serpJobCardInfoDensity1: Boolean(
    cookies.SERP_JOBCARD_INFO_DENSITY_1 ||
      SERP_JOBCARD_INFO_DENSITY_1 ||
      toggleExperiment({
        experimentId: 'serpJobCardInfoDensity1',
        experiments,
        cookieValue: cookies.SERP_JOBCARD_INFO_DENSITY_1,
      }),
  ),
  hideCompanyLogo: Boolean(cookies.HIDE_COMPANY_LOGO),
  entryLevelBadge: Boolean(cookies.ENTRY_LEVEL_BADGE || ENTRY_LEVEL_BADGE),
  competitivePlacementEmbeddedJdv: true,
  competitivePlacement: true,
  homeFeedAPI: Boolean(cookies.HOME_FEED_API),
  careerFeedCompanyEvp: Boolean(cookies.CAREER_FEED_COMPANY_EVP),
});

export const updateFeatureFlagsAfterAuth = (
  cookies: CookieType = {},
  experiments: TSeekExperiments,
  zoneFeatures: ZoneFeatures,
): UpdateFeatureFlagsAction => ({
  type: UPDATE_FEATURE_FLAGS,
  payload: {
    dynamicFlags: {
      homepageRecsBadgingStrongApplicant: Boolean(
        cookies.HOMEPAGE_RECS_BADGING_STRONG_APPLICANT ||
          zoneFeatures.HOMEPAGE_RECS_BADGING_STRONG_APPLICANT ||
          toggleExperiment({
            experimentId: 'homepageRecsBadgingStrongApplicant',
            experiments,
            cookieValue: cookies.HOMEPAGE_RECS_BADGING_STRONG_APPLICANT,
          }),
      ),
      homepageGenAIChat: toggleExperiment({
        experimentId: 'homepageGenAIChat',
        experiments,
        cookieValue: cookies.HOMEPAGE_GENAI_CHAT,
      }),
      homepageGenAIChatAltModel: Boolean(cookies.HOMEPAGE_GENAI_CHAT_ALT_MODEL),
      homepageGenAIChatAltPosition: Boolean(
        cookies.HOMEPAGE_GENAI_CHAT_ALT_POSITION,
      ),
      homepageGenAIChatMobileDrawer: Boolean(
        cookies.HOMEPAGE_GENAI_CHAT_MOBILE_DRAWER,
      ),
      homeFeedAPI:
        Boolean(cookies.HOME_FEED_API) ||
        toggleExperiment({
          experimentId: 'homeFeedAPI',
          experiments,
          cookieValue: cookies.HOME_FEED_API,
        }),
      homepageGenAIChatDesktopDrawer: Boolean(
        cookies.HOMEPAGE_GENAI_CHAT_DESKTOP_DRAWER,
      ),
    },
  },
});

export const overrideFeatureFlag = ({
  flag,
  value,
}: {
  flag: string;
  value?: string;
}): OverrideFeatureFlagAction => ({
  type: OVERRIDE_FEATURE_FLAG,
  payload: {
    flag,
    value,
  },
});

export default function reducer(
  state = initialState,
  action: TypedAction,
): FeatureFlagsState {
  switch (action.type) {
    case UPDATE_FEATURE_FLAGS: {
      const { dynamicFlags } = action.payload;
      return {
        ...state,
        ...dynamicFlags,
      };
    }

    case OVERRIDE_FEATURE_FLAG: {
      const { flag, value } = action.payload;
      const overrides = { ...state.overrides };
      if (typeof value === 'undefined') {
        delete overrides[flag];
      } else {
        overrides[flag] = value;
      }
      return {
        ...state,
        overrides,
      };
    }

    default:
      return state;
  }
}
